/**
 * The top of the tree: where the browser is, who is at it, and therefore what
 * is allowed on screen.
 *
 * View is pure, so the gate can be rendered and tested without a browser. App
 * wraps it with the live session and the live location.
 */

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { Profile } from "@ls/shared";
import { http, setTokenSource } from "../api";
import { BRAND_NAME as BRAND, WORDMARK } from "../brand";
import { AuthLayout, Boot } from "../auth/AuthLayout";
import { loadClerk, type ClerkLike } from "../auth/clerk";
import { readHint } from "../auth/hint";
import { Awaiting } from "../auth/Awaiting";
import { ProfileStep } from "../auth/ProfileStep";
import { CREATE_ACCOUNT_PATH, SSO_CALLBACK_PATH, SignIn, type SignedOutPage } from "../auth/SignIn";
import { Button } from "../ui";
import { SessionContext, type SessionApi } from "./context";
import { matchPath, type Match } from "./routes";
import { NotFound, PublicBrand } from "./pages";
import { navigate, usePlace, useScrollAfterNavigation } from "./router";
import { resolveGate, signOut, type Gate } from "./session";
import { Shell } from "./Shell";

export interface ViewProps {
  readonly gate: Gate;
  readonly match: Match;
  /** Where the browser is: /create-account is Create your account to a signed out browser. */
  readonly pathname?: string;
  readonly query: URLSearchParams;
  readonly placeKey: string;
  readonly session: SessionApi | null;
  /** Clerk, once loaded: null while it is still starting, which the sign in form allows for. */
  readonly clerk: ClerkLike | null;
  readonly onSignedIn: () => void | Promise<void>;
  readonly onProfileCreated: (profile: Profile, access: "pending" | "approved") => void;
  readonly onSignOut: () => void;
  readonly onRetry: () => void;
}

/** Which signed out page an address is: Create your account at its own address, Sign in everywhere else. */
export function signedOutPage(pathname: string): SignedOutPage {
  return pathname === CREATE_ACCOUNT_PATH ? "create" : "sign_in";
}

/**
 * What renders, as a function of the gate and the route and nothing else.
 *
 *   public route    the page on its own, for anyone, never the frame
 *   loading         the brand on an empty sheet
 *   unavailable     the signed out frame, saying the API could not be reached
 *   signed out      the signed out frame, with sign in
 *   needs profile   the signed out frame, with the profile step
 *   awaiting        the signed out frame, saying the request is with an admin
 *   signed in       the app frame, with the page or "not found"
 *
 * The frame appears in exactly one branch, and that branch needs a profile.
 */
export function View(props: ViewProps) {
  const { gate, match } = props;
  const page = signedOutPage(props.pathname ?? "/");

  // /create-account is a page of its own only while nobody is signed in.
  if (match.kind === "redirect" && !(page === "create" && gate.kind !== "signed_in")) return null;

  if (match.kind === "route" && match.route.access === "public") {
    const Page = match.route.page;
    return (
      <div class="public">
        <PublicBrand />
        <main class="public-main" id="content" tabIndex={-1}>
          <Page params={match.params} query={props.query} />
        </main>
      </div>
    );
  }

  if (gate.kind === "loading") return <Boot />;

  if (gate.kind === "unavailable") {
    return (
      <AuthLayout portal>
        <div class="auth-step">
          <h1>
            {WORDMARK.lead}
            {WORDMARK.tail} is not reachable
          </h1>
          <p class="auth-lede">Check your connection and try again. If it keeps happening, the service may be down for a few minutes.</p>
          <p class="mono small muted auth-detail">{gate.detail}</p>
          <Button variant="primary" block onClick={props.onRetry}>
            Try again
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (gate.kind === "needs_profile") {
    return (
      <AuthLayout compact portal>
        <ProfileStep
          email={gate.email}
          onCreated={props.onProfileCreated}
          onRecheck={props.onRetry}
          onUseAnotherAccount={props.onSignOut}
        />
      </AuthLayout>
    );
  }

  if (gate.kind === "awaiting_approval") {
    return (
      <AuthLayout compact portal>
        <Awaiting email={gate.email} name={gate.name} onCheck={props.onRetry} onSignOut={props.onSignOut} />
      </AuthLayout>
    );
  }

  if (gate.kind === "signed_out" || !props.session) {
    // Drawn without waiting for Clerk (props.clerk is null until it loads): the
    // form is usable at once, and what it does asks Clerk when it runs.
    const reason = gate.kind === "signed_out" ? gate.reason : null;
    return (
      <AuthLayout portal>
        <SignIn
          key={reason ?? "none"}
          clerk={props.clerk}
          reason={reason}
          page={page}
          onPage={(next) => navigate(next === "create" ? CREATE_ACCOUNT_PATH : "/")}
          onSignedIn={props.onSignedIn}
        />
      </AuthLayout>
    );
  }

  return (
    <SessionContext.Provider value={props.session}>
      <Shell active={match.kind === "route" ? match.route.id : null} placeKey={props.placeKey}>
        {match.kind === "route" ? <match.route.page params={match.params} query={props.query} /> : <NotFound />}
      </Shell>
    </SessionContext.Provider>
  );
}

/** How long a returning tab waits between asking whether its sign in still stands. */
const RECHECK_MS = 15_000;

/** The live app. main.tsx starts the router before mounting it. */
export function App() {
  const place = usePlace();
  const match = useMemo(() => matchPath(place.pathname), [place.pathname]);
  const query = useMemo(() => new URLSearchParams(place.search), [place.search]);
  const placeKey = `${place.pathname}${place.search}`;
  // What Clerk's own cookies say before Clerk has loaded (auth/hint.ts). Read
  // once, at the first render: they decide what to draw first and whether the
  // API can be asked at once, and the gate below corrects the page if they were
  // wrong. Coming back from Google, the cookies say nobody is signed in until
  // Clerk has finished the sign in, so that one page waits as it always did.
  const [hint] = useState(() => readHint());
  const returning = window.location.pathname === SSO_CALLBACK_PATH;
  const [gate, setGate] = useState<Gate>(() =>
    hint.signedOut && !returning ? { kind: "signed_out", reason: null } : { kind: "loading" },
  );
  const [clerk, setClerk] = useState<ClerkLike | null>(null);
  const gateRef = useRef(gate);
  gateRef.current = gate;
  /** Set while this tab is signing itself out, so Clerk's listener does not call it "ended". */
  const leaving = useRef(false);

  const refresh = () => resolveGate(http, loadClerk).then(setGate);

  // The gate and Clerk start together. A student coming back to a live session
  // has the API asked with the token in the cookie while Clerk's script is
  // still starting, and sees the app without waiting for it; everyone else is
  // decided once Clerk says. Coming back from Google, Clerk finishes the sign
  // in first, and only then does the gate ask who is here.
  useEffect(() => {
    let stop = () => {};
    if (!returning) void resolveGate(http, loadClerk, hint.token).then(setGate);
    void (async () => {
      try {
        const loaded = await loadClerk();
        setTokenSource(async () => (loaded.session ? await loaded.session.getToken() : null));
        setClerk(loaded);
        if (returning) {
          try {
            await loaded.handleRedirectCallback(
              { signInFallbackRedirectUrl: "/", signUpFallbackRedirectUrl: "/" },
              async () => navigate("/", { replace: true }),
            );
          } catch {
            // A callback that cannot finish leaves nobody signed in, which the
            // gate below shows as the sign in screen.
          }
          navigate("/", { replace: true });
        }
        // Clerk ends a session this tab did not end, as when the one device
        // rule signs this device out and its token stops refreshing: say so.
        stop = loaded.addListener(() => {
          if (loaded.session || leaving.current) return;
          const kind = gateRef.current.kind;
          if (kind === "signed_in" || kind === "needs_profile" || kind === "awaiting_approval") {
            setGate({ kind: "signed_out", reason: "session_ended" });
          }
        });
      } catch {
        // resolveGate reports it.
      }
      if (returning) await refresh();
    })();
    return () => stop();
  }, []);

  // An old spelling of a route becomes the current one without a history entry.
  // /create-account becomes the Dashboard once somebody is signed in.
  useEffect(() => {
    if (match.kind !== "redirect") return;
    if (signedOutPage(place.pathname) === "create" && gate.kind !== "signed_in") return;
    navigate(`${match.to}${place.search}`, { replace: true });
  }, [match, place.search, place.pathname, gate.kind]);

  useScrollAfterNavigation(place);

  // The one device rule means a sign in can end while its tab sits in the
  // background: the student signed in on their phone. When the tab comes back,
  // ask once, and if it has gone, say why instead of letting the next click
  // fail. Throttled, because a student flicking between tabs should not cost a
  // request each time.
  useEffect(() => {
    let last = Date.now();
    const recheck = async () => {
      // A waiting account is checked too, so a tab left open lets its student
      // in once they have been approved, without a click.
      const kind = gateRef.current.kind;
      if (document.visibilityState !== "visible" || (kind !== "signed_in" && kind !== "awaiting_approval")) return;
      if (Date.now() - last < RECHECK_MS) return;
      last = Date.now();
      const next = await resolveGate(http, loadClerk);
      // Offline for a moment is not signed out. The next check will tell.
      if (next.kind !== "unavailable") setGate(next);
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);

  useEffect(() => {
    const title = match.kind === "route" ? match.route.title : match.kind === "not_found" ? "Page not found" : null;
    const signedOut = gate.kind !== "signed_in" && !(match.kind === "route" && match.route.access === "public");
    const away = signedOutPage(place.pathname) === "create" ? "Create your account" : "Sign in";
    document.title = signedOut ? `${away} · ${BRAND}` : title && title !== "Dashboard" ? `${title} · ${BRAND}` : BRAND;
  }, [match, gate.kind, place.pathname]);

  // Clerk may still be loading when the student asks to leave, as it can be
  // for a page that opened straight onto a live session, so ask for it here.
  const leave = () => {
    leaving.current = true;
    void loadClerk()
      .then((loaded) => signOut(loaded))
      .then((next) => setGate(next))
      .catch(() => {
        // Clerk cannot be reached, so nothing can be ended there. Say nothing
        // new: the student is still signed in, and can try again.
      })
      .finally(() => {
        leaving.current = false;
      });
  };

  const session = useMemo<SessionApi | null>(() => {
    if (gate.kind !== "signed_in") return null;
    return {
      profile: gate.profile,
      plan: gate.plan,
      setProfile: (profile: Profile) => setGate({ kind: "signed_in", profile, plan: gate.plan }),
      refresh: async () => {
        const next = await resolveGate(http, loadClerk);
        // Offline for a moment is not signed out: the page keeps what it had.
        if (next.kind !== "unavailable") setGate(next);
      },
      signOut: leave,
    };
  }, [gate, clerk]);

  return (
    <View
      gate={gate}
      match={match}
      pathname={place.pathname}
      query={query}
      placeKey={placeKey}
      session={session}
      clerk={clerk}
      // Straight to the next step, with the sign in form still up meanwhile:
      // blanking to the loading sheet here threw away the whole screen and
      // built it again a moment later.
      onSignedIn={refresh}
      // The account is open on Free (or on an intern's Elite): ask /me for
      // its plan and go straight to the Dashboard, the form up meanwhile.
      // Waiting for an admin applies only if approval is turned back on.
      onProfileCreated={(profile, access) => {
        if (access === "pending") setGate({ kind: "awaiting_approval", email: profile.email, name: profile.name });
        else {
          if (window.location.pathname !== "/") navigate("/", { replace: true });
          void refresh();
        }
      }}
      onSignOut={leave}
      onRetry={() => {
        setGate({ kind: "loading" });
        void refresh();
      }}
    />
  );
}
